import { Capacitor } from '@capacitor/core';
import { CSSProperties, RefObject, useEffect } from 'react';

// On iOS the store app keeps the web view at full size when the on-screen
// keyboard opens (capacitor.config.ts, Keyboard resize "none"), and so does
// Android 15 and later (edge to edge), so the keyboard draws on top of the
// page and nothing moves out of its way. On a tablet in landscape it covers
// the lower half of the auth card: the password field and the main button end
// up under it. Android 14 and earlier shrink the web view for the keyboard
// instead, so only the part of the keyboard that still overlaps the view
// counts. Capacitor's Keyboard plugin reports the keyboard's height through
// these window events on iOS and Android; a plain browser never sends them, so
// the web site is unaffected.
//
// The page starts moving on keyboardWillShow, which iOS sends as the keyboard
// starts to rise, and slides with the keyboard. keyboardDidShow, sent once the
// keyboard is up, gives its final height and corrects the move if needed.
const KEYBOARD_WILL_SHOW = 'keyboardWillShow';
const KEYBOARD_DID_SHOW = 'keyboardDidShow';
// The page starts back down with the keyboard on keyboardWillHide. A tap on
// the page's button is safe: the tap's click reaches the button before the
// plugin's event arrives (it comes from the app's native side, after the
// field has already lost focus).
const KEYBOARD_WILL_HIDE = 'keyboardWillHide';
const KEYBOARD_DID_HIDE = 'keyboardDidHide';

/** Gap kept between the keyboard and what sits just above it, and above the top edge. */
export const KEYBOARD_CLEARANCE_PX = 16;

/** How the page slides to a new position. */
export interface PageMotion {
  durationMs: number;
  easing: string;
  /** How long after the event the slide starts. */
  delayMs: number;
}

// The iOS keyboard's own slide, which the page copies so that it looks pushed
// by the keyboard: fast at first, then easing into place. The plugin's events
// do not carry the keyboard's timing, so it is written down here, measured on
// the iOS 26 Simulators (keyboard 95% up about 190 ms after it starts, settled
// about 300 ms; down in about 180 ms).
const IOS_KEYBOARD_EASING = 'cubic-bezier(0.38, 0.7, 0.125, 1)';

/**
 * The page's slide up with the iOS keyboard. As the keyboard starts to rise
 * the app is busy for a moment before it draws again (about 50 ms on the iPad
 * Pro 11" Simulator, 70 to 110 ms on the iPhone 17 Simulator); a slide timed
 * from the event would show its first part in one step. So the slide is not
 * timed from the event: it starts once the app draws again, on the
 * KEYBOARD_SHOW_START_FRAMES-th animation frame after keyboardWillShow, and so
 * follows however long the device is busy.
 */
export const IOS_KEYBOARD_SHOW_MOTION: PageMotion = {
  durationMs: 300,
  easing: IOS_KEYBOARD_EASING,
  delayMs: 0,
};

/**
 * The animation frame after keyboardWillShow on which the iOS slide up
 * starts. The first frame's callbacks run before that frame is drawn, while
 * the app can still be busy; the second come only once it has drawn again.
 */
export const KEYBOARD_SHOW_START_FRAMES = 2;

/** The page's slide back down with the iOS keyboard. */
export const IOS_KEYBOARD_HIDE_MOTION: PageMotion = {
  durationMs: 250,
  easing: IOS_KEYBOARD_EASING,
  delayMs: 0,
};

/**
 * A short slide for a move whose timing the keyboard does not give: on
 * Android, where the plugin sends keyboardWillShow and keyboardWillHide as the
 * keyboard's own animation starts and keyboardDidShow and keyboardDidHide as
 * it ends, but not the animation's length or curve, and for a correction once
 * the keyboard has settled.
 */
export const SHORT_MOTION: PageMotion = {
  durationMs: 200,
  easing: 'ease-out',
  delayMs: 0,
};

interface KeyboardHeightEvent extends Event {
  keyboardHeight?: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface KeyboardLiftInput {
  /** Top and bottom of the focused field, as drawn now (current lift included). */
  focusedTop: number;
  focusedBottom: number;
  /** Bottom of the focused field's form button, as drawn now; null when there is none. */
  submitBottom: number | null;
  /** The web view's height now. */
  viewportHeight: number;
  /** The web view's height with no keyboard up. */
  restingViewportHeight: number;
  keyboardHeight: number;
  /** How far the page is moved up right now. */
  currentLift: number;
}

/**
 * How far to move the page up so the focused field sits above the keyboard,
 * together with its form's button when both fit; never so far that the focused
 * field's top leaves the screen. When the web view has already shrunk for the
 * keyboard, the keyboard's top is the view's bottom.
 */
export const keyboardLift = ({
  focusedTop,
  focusedBottom,
  submitBottom,
  viewportHeight,
  restingViewportHeight,
  keyboardHeight,
  currentLift,
}: KeyboardLiftInput): number => {
  if (keyboardHeight <= 0) return 0;
  const keyboardTop = Math.min(viewportHeight, restingViewportHeight - keyboardHeight);
  const visibleBottom = keyboardTop - KEYBOARD_CLEARANCE_PX;
  const restingTop = focusedTop + currentLift;
  const restingBottom = focusedBottom + currentLift;
  const restingFormBottom =
    submitBottom === null ? restingBottom : Math.max(restingBottom, submitBottom + currentLift);
  const highestLift = restingTop - KEYBOARD_CLEARANCE_PX;
  return Math.max(0, Math.round(Math.min(restingFormBottom - visibleBottom, highestLift)));
};

export interface WillShowHeightInput {
  /** The height keyboardWillShow reports. */
  reportedHeight: number;
  /** Whether the app runs on an iPad, where the plugin over-reports it. */
  onIPad: boolean;
  /** The web view's size now. */
  viewport: ViewportSize;
  /** The screen's size (window.screen), in either orientation. */
  screen: ViewportSize;
}

/**
 * The most the web view can sit below the top of a screen it fills: the
 * iPad's status bar (20 to 32 px). A larger gap means a window shorter than
 * the screen, not a status bar.
 */
const MAX_FULL_SCREEN_WEB_VIEW_TOP_PX = 48;

/**
 * How much of the web view the keyboard will cover, from keyboardWillShow.
 * On an iPad the plugin counts the web view's distance from the top of its
 * window twice, so its height is too large by that distance (32 px on the iPad
 * Pro 11, 460 for a 428 px keyboard). When the app fills the screen, the web
 * view is as wide as the screen is in one orientation, and that distance is
 * the screen's other side less the web view's height, no more than a status
 * bar. In a window that does not fill the screen (Split View, Slide Over, a
 * Stage Manager window, full width or not) the distance is not known, so the
 * height is kept as reported: a little too large, until keyboardDidShow, which
 * reports the keyboard's own height, corrects it. Taking off the whole gap
 * below a shorter window would leave the keyboard counted far too small.
 */
export const keyboardHeightFromWillShow = ({
  reportedHeight,
  onIPad,
  viewport,
  screen,
}: WillShowHeightInput): number => {
  if (!onIPad) return reportedHeight;
  const screenHeight =
    viewport.width === screen.width
      ? screen.height
      : viewport.width === screen.height
        ? screen.width
        : null;
  if (screenHeight === null) return reportedHeight;
  const webViewTop = screenHeight - viewport.height;
  return webViewTop > 0 && webViewTop <= MAX_FULL_SCREEN_WEB_VIEW_TOP_PX
    ? reportedHeight - webViewTop
    : reportedHeight;
};

/**
 * The web view's height with no keyboard up, updated from a new measurement.
 * A new width is a turn or a new window: take it as it is. Otherwise a change
 * only counts while the keyboard is closed, and a shrink only while no field
 * has focus (a focused field means the keyboard is on its way, and Android 14
 * and earlier shrink the view for it before the plugin reports it).
 */
export const nextRestingViewport = (
  resting: ViewportSize,
  measured: ViewportSize,
  { keyboardOpen, fieldFocused }: { keyboardOpen: boolean; fieldFocused: boolean },
): ViewportSize => {
  if (measured.width !== resting.width) return measured;
  if (keyboardOpen) return resting;
  return measured.height >= resting.height || !fieldFocused ? measured : resting;
};

export interface KeyboardScrollInput {
  /** The lift the page would need if nothing scrolled (keyboardLift's answer). */
  neededLift: number;
  /** Top of the focused field and of its scrolling box, as drawn now. */
  focusedTop: number;
  scrollerTop: number;
  /** How far the box is scrolled now, and how far it can scroll at most. */
  scrollTop: number;
  scrollMax: number;
}

/**
 * How far to scroll the focused field's scrolling box before the page is
 * moved: as far as the keyboard needs, but not so far that the field passes
 * the box's top edge or the box runs out of content. The page is then moved
 * up only for what is left, so a box that ends at the bottom of the screen
 * cannot cut off the field or its button. Negative brings back a field that
 * sits above the box's top edge.
 */
export const keyboardScroll = ({
  neededLift,
  focusedTop,
  scrollerTop,
  scrollTop,
  scrollMax,
}: KeyboardScrollInput): number => {
  const roomAboveField = focusedTop - scrollerTop - KEYBOARD_CLEARANCE_PX;
  const roomBelowContent = scrollMax - scrollTop;
  const scroll = Math.min(neededLift, roomAboveField, roomBelowContent);
  return Math.round(Math.max(-scrollTop, scroll));
};

/** Where the page is moved to, and how it slides there; null slides nothing. */
export interface PageMove {
  lift: number;
  motion: PageMotion | null;
}

export const PAGE_AT_REST: PageMove = { lift: 0, motion: null };

/**
 * The style for the page's outermost box: a transform while the page is moved
 * up, and nothing at rest (a transform changes what fixed boxes inside it are
 * placed against). Sliding back down keeps only the transition, so the page
 * slides from where it is to no transform at all.
 */
export const pageMoveStyle = ({ lift, motion }: PageMove): CSSProperties => {
  const delay = motion && motion.delayMs > 0 ? ` ${motion.delayMs}ms` : '';
  const transition = motion
    ? { transition: `transform ${motion.durationMs}ms ${motion.easing}${delay}` }
    : {};
  return lift > 0 ? { transform: `translateY(-${lift}px)`, ...transition } : transition;
};

/**
 * The next move: a new lift slides with the given motion; the same lift
 * changes nothing, so a slide already running is not restarted.
 */
export const nextPageMove = (current: PageMove, lift: number, motion: PageMotion): PageMove =>
  lift === current.lift ? current : { lift, motion };

/**
 * Draws a move on the page's box. Written straight to the box, not through
 * React state: the page is not re-rendered for each keyboard event while the
 * keyboard is starting to move.
 */
export const drawPageMove = (page: HTMLElement, move: PageMove): void => {
  const style = pageMoveStyle(move);
  page.style.transform = style.transform ?? '';
  page.style.transition = style.transition ?? '';
};

/**
 * A motion's easing as a function of time (0 to 1) giving how far along the
 * move is (0 to 1), for a move drawn frame by frame: a CSS cubic-bezier() or
 * ease-out. Anything else moves at an even pace.
 */
export const easingCurve = (easing: string): ((time: number) => number) => {
  const bezier = /^cubic-bezier\(([^)]*)\)$/.exec(easing.trim());
  const points = bezier
    ? bezier[1].split(',').map(Number)
    : easing.trim() === 'ease-out'
      ? [0, 0, 0.58, 1]
      : null;
  if (!points || points.length !== 4 || points.some((point) => !Number.isFinite(point))) {
    return (time) => Math.min(1, Math.max(0, time));
  }
  const [x1, y1, x2, y2] = points;
  // One coordinate of the curve at parameter t, from its two control points.
  const along = (first: number, second: number, t: number) =>
    3 * first * t * (1 - t) ** 2 + 3 * second * t ** 2 * (1 - t) + t ** 3;
  // The curve's parameter at this time, by halving (x rises with t).
  const parameterAt = (time: number): number =>
    Array.from({ length: 24 }).reduce<[number, number]>(
      ([low, high]) => {
        const middle = (low + high) / 2;
        return along(x1, x2, middle) < time ? [middle, high] : [low, middle];
      },
      [0, 1],
    )[1];
  return (time) => {
    if (time <= 0) return 0;
    if (time >= 1) return 1;
    return along(y1, y2, parameterAt(time));
  };
};

const isTextEntry = (element: EventTarget | null): element is HTMLElement =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  (element instanceof HTMLElement && element.isContentEditable);

/**
 * How far up a transform moves a box: a computed matrix (what a browser
 * reports, mid-slide included) or the translateY written in the page's style.
 */
export const liftFromTransform = (transform: string): number => {
  const matrix = /^matrix\(([^)]*)\)$/.exec(transform);
  if (matrix) return -Number(matrix[1].split(',')[5]);
  const matrix3d = /^matrix3d\(([^)]*)\)$/.exec(transform);
  if (matrix3d) return -Number(matrix3d[1].split(',')[13]);
  const translate = /translateY\((-?[\d.]+)px\)/.exec(transform);
  return translate ? -Number(translate[1]) : 0;
};

// The lift the page is drawn with right now, mid-slide included. Read from the
// page itself, not from the last value set: a measurement can run (a resize,
// the form observer) while the page is still sliding, and the boxes it reads
// are as drawn.
const drawnLift = (page: HTMLElement): number => {
  const computed = getComputedStyle(page).transform;
  const lift = liftFromTransform(computed || page.style.transform);
  return Number.isFinite(lift) ? Math.max(0, lift) : 0;
};

// Whether a box lets its content scroll up and down. Only the shorthand is
// filled in where the style is not fully computed (jsdom).
const scrollsVertically = (node: HTMLElement): boolean => {
  const style = getComputedStyle(node);
  return /^(auto|scroll)$/.test(style.overflowY || style.overflow);
};

// The nearest box between the field and the page (the page included) that
// scrolls its content.
const scrollingBoxOf = (field: HTMLElement, page: HTMLElement): HTMLElement | null => {
  const ancestors = (node: HTMLElement | null): HTMLElement[] =>
    !node ? [] : node === page ? [node] : [node, ...ancestors(node.parentElement)];
  return (
    ancestors(field.parentElement).find(
      (node) => scrollsVertically(node) && node.scrollHeight > node.clientHeight,
    ) ?? null
  );
};

export interface KeepFocusedFieldAboveKeyboardOptions {
  /**
   * Scroll the focused field's scrolling box first and move the page only for
   * what is left (a page whose forms sit in a scrolling box under a fixed
   * header, such as Settings > Account).
   */
  scrollFirst?: boolean;
}

const currentViewport = (): ViewportSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const currentScreen = (): ViewportSize => ({
  width: window.screen?.width ?? 0,
  height: window.screen?.height ?? 0,
});

// An iPad reports itself as a Mac to the web view, but one with a touch screen.
const runsOnIPad = (): boolean =>
  /iPad/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1);

/**
 * Keeps the focused field of an auth page above the on-screen keyboard in the
 * store apps. Moves the page's outermost box (pageRef) up with a transform,
 * the whole page with it (background, card and anything inside it, the
 * sign-in spinners included), while the keyboard covers the focused field or
 * its form's button, sliding with the keyboard, and leaves no style on it
 * otherwise. What shows below the moved page is the app's dark body
 * background, under the keyboard.
 */
export const useKeepFocusedFieldAboveKeyboard = (
  pageRef: RefObject<HTMLElement | null>,
  { scrollFirst = false }: KeepFocusedFieldAboveKeyboardOptions = {},
): void => {
  useEffect(() => {
    const onIOS = Capacitor.getPlatform() === 'ios';
    const onIPad = onIOS && runsOnIPad();
    // The keyboard's own slide where it is known (iOS), else a short one.
    const keyboardShowMotion = onIOS ? IOS_KEYBOARD_SHOW_MOTION : SHORT_MOTION;
    const keyboardHideMotion = onIOS ? IOS_KEYBOARD_HIDE_MOTION : SHORT_MOTION;
    let keyboardHeight = 0;
    // keyboardWillHide has come and keyboardDidHide not yet.
    let keyboardClosing = false;
    let resting = currentViewport();
    // The field the page was last moved for.
    let measuredField: HTMLElement | null = null;
    let move = PAGE_AT_REST;
    // The box last drawn on: still the page's after a Settings tab switch has
    // taken the ref away from it.
    let drawnPage: HTMLElement | null = null;
    let restTimer = 0;
    // Where the document was scrolled as the keyboard started up (iOS). iOS
    // scrolls the document to show a field the rising keyboard covers, and
    // scrolls it back once the page has moved the field clear. The page is
    // measured as if the document were still where it was, so a measurement
    // taken in between does not leave the page too low once iOS scrolls back.
    let documentScrollAtShow: number | null = null;
    // The animation frame the iOS slide up waits for.
    let showFrame = 0;
    // The scrolling box's own slide (Settings > Account), drawn frame by
    // frame with the page's motion, so the box and the page move and arrive
    // together. Not left to the browser's smooth scroll: a field taking focus
    // stops that, and the box's position as the page reads it trails the one
    // drawn by about a frame, so the page could not tell where it would stop.
    let boxSlide: {
      box: HTMLElement;
      from: number;
      target: number;
      motion: PageMotion;
      curve: (time: number) => number;
      startedAt: number | null;
      // Where the slide last put the box.
      drawnAt: number;
    } | null = null;
    let slideFrame = 0;

    const stepBoxSlide = (now: number) => {
      if (!boxSlide) return;
      const slide = boxSlide;
      slide.startedAt = slide.startedAt ?? now;
      const { durationMs, delayMs } = slide.motion;
      const time = durationMs > 0 ? (now - slide.startedAt - delayMs) / durationMs : 1;
      const along = slide.curve(time);
      slide.box.scrollTop = slide.from + (slide.target - slide.from) * along;
      slide.drawnAt = slide.box.scrollTop;
      if (time < 1) slideFrame = requestAnimationFrame(stepBoxSlide);
      else boxSlide = null;
    };
    const slideBoxTo = (box: HTMLElement, target: number, motion: PageMotion) => {
      boxSlide = {
        box,
        from: box.scrollTop,
        target,
        motion,
        curve: easingCurve(motion.easing),
        startedAt: null,
        drawnAt: box.scrollTop,
      };
      cancelAnimationFrame(slideFrame);
      slideFrame = requestAnimationFrame(stepBoxSlide);
    };
    // A person scrolling the box takes it over: the slide stops there.
    const boxTakenOver = () => {
      boxSlide = null;
      cancelAnimationFrame(slideFrame);
    };
    // A person scrolling the page or its box: measured from where it is now.
    const userScrolled = () => {
      documentScrollAtShow = null;
      boxTakenOver();
    };
    // The slide still under way in this box. A box that something else has
    // scrolled since (the browser showing a field as it takes focus) is no
    // longer on that slide: it is measured from where it is.
    const boxSlideOf = (box: HTMLElement) => {
      if (!boxSlide || boxSlide.box !== box) return null;
      if (Math.abs(box.scrollTop - boxSlide.drawnAt) < 1) return boxSlide;
      boxTakenOver();
      return null;
    };

    function show(next: PageMove) {
      if (next === move) return;
      move = next;
      const page = pageRef.current ?? drawnPage;
      if (!page) return;
      if (drawnPage && drawnPage !== page) drawPageMove(drawnPage, PAGE_AT_REST);
      drawPageMove(page, move);
      drawnPage = page;
      clearTimeout(restTimer);
      // Once the page has slid back down, its transition goes.
      if (move.lift > 0 || !move.motion) return;
      const { durationMs, delayMs } = move.motion;
      restTimer = window.setTimeout(() => show(PAGE_AT_REST), delayMs + durationMs + 50);
    }
    const moveTo = (lift: number, motion: PageMotion) => show(nextPageMove(move, lift, motion));

    const focusedField = (): HTMLElement | null => {
      const page = pageRef.current;
      const focused = document.activeElement;
      return page && isTextEntry(focused) && page.contains(focused) ? focused : null;
    };

    const recordResting = (fieldFocused: boolean) => {
      resting = nextRestingViewport(resting, currentViewport(), {
        keyboardOpen: keyboardHeight > 0,
        fieldFocused,
      });
    };

    // A form whose size changes while the keyboard is up (an error line above
    // the button) is measured again, and so is one that leaves the page (a
    // Settings tab switch). Not every web view has ResizeObserver.
    const formObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => measure(SHORT_MOTION));

    // Focus on something other than a field (the button being tapped) keeps
    // the page where it is; a field that has left the page needs no move.
    function measure(motion: PageMotion) {
      if (keyboardHeight <= 0 || (measuredField && !measuredField.isConnected)) {
        measuredField = null;
        moveTo(0, motion);
        return;
      }
      const page = pageRef.current;
      const field = focusedField();
      if (!page || !field) return;
      measuredField = field;
      const form = field.closest('form');
      if (form) formObserver?.observe(form);
      const submit = form?.querySelector('button[type="submit"]');
      const scroller = scrollFirst ? scrollingBoxOf(field, page) : null;
      // While the box is still sliding to where an earlier move sent it, work
      // from where it will stop. The field, and the button when it is in the
      // box, are shifted to where they will be drawn then.
      const slide = scroller ? boxSlideOf(scroller) : null;
      const scrollTop = slide ? slide.target : (scroller?.scrollTop ?? 0);
      const pending = slide && scroller ? slide.target - scroller.scrollTop : 0;
      const submitInBox = !!(submit && scroller?.contains(submit));
      const documentShift =
        documentScrollAtShow === null ? 0 : window.scrollY - documentScrollAtShow;
      const fieldBox = field.getBoundingClientRect();
      const fieldTop = fieldBox.top - pending + documentShift;
      const fieldBottom = fieldBox.bottom - pending + documentShift;
      const drawnSubmitBottom = submit
        ? submit.getBoundingClientRect().bottom + documentShift
        : null;
      const submitBottom =
        drawnSubmitBottom !== null && submitInBox ? drawnSubmitBottom - pending : drawnSubmitBottom;
      const currentLift = drawnLift(page);
      // The page's lift once the box has scrolled by `scrolled` more: the
      // field, and the button when it is in the box, sit that much higher.
      const liftAfterScroll = (scrolled: number) =>
        keyboardLift({
          focusedTop: fieldTop - scrolled,
          focusedBottom: fieldBottom - scrolled,
          submitBottom:
            submitBottom !== null && submitInBox ? submitBottom - scrolled : submitBottom,
          viewportHeight: window.innerHeight,
          restingViewportHeight: resting.height,
          keyboardHeight,
          currentLift,
        });
      const scrollMax = scroller ? scroller.scrollHeight - scroller.clientHeight : 0;
      const scroll = scroller
        ? keyboardScroll({
            neededLift: liftAfterScroll(0),
            focusedTop: fieldTop,
            scrollerTop: scroller.getBoundingClientRect().top + documentShift,
            scrollTop,
            scrollMax,
          })
        : 0;
      const target = Math.min(scrollMax, Math.max(0, scrollTop + scroll));
      // The box slides to its new place, as the page does. The page's lift is
      // worked out from where the box will stop, so the two arrive together.
      if (scroller && target !== scrollTop) slideBoxTo(scroller, target, motion);
      moveTo(liftAfterScroll(target - scrollTop), motion);
    }

    // Runs `start` on the given animation frame from now.
    const onAnimationFrame = (frames: number, start: () => void) => {
      cancelAnimationFrame(showFrame);
      showFrame = requestAnimationFrame(() =>
        frames > 1 ? onAnimationFrame(frames - 1, start) : start(),
      );
    };

    const keyboardGoing = (motion: PageMotion) => {
      documentScrollAtShow = null;
      cancelAnimationFrame(showFrame);
      keyboardHeight = 0;
      measuredField = null;
      formObserver?.disconnect();
      moveTo(0, motion);
    };

    const reportedHeight = (event: Event): number | null => {
      const height = (event as KeyboardHeightEvent).keyboardHeight;
      return typeof height === 'number' && height > 0 ? height : null;
    };

    const onWillShow = (event: Event) => {
      const height = reportedHeight(event);
      if (height === null) return;
      keyboardClosing = false;
      if (onIOS && keyboardHeight <= 0) documentScrollAtShow = window.scrollY;
      keyboardHeight = keyboardHeightFromWillShow({
        reportedHeight: height,
        onIPad,
        viewport: currentViewport(),
        screen: currentScreen(),
      });
      if (onIOS) onAnimationFrame(KEYBOARD_SHOW_START_FRAMES, () => measure(keyboardShowMotion));
      else measure(keyboardShowMotion);
    };
    const onDidShow = (event: Event) => {
      const height = reportedHeight(event);
      if (height === null) return;
      keyboardClosing = false;
      keyboardHeight = height;
      measure(SHORT_MOTION);
    };
    const onWillHide = () => {
      keyboardClosing = true;
      keyboardGoing(keyboardHideMotion);
    };
    const onDidHide = () => {
      keyboardClosing = false;
      keyboardGoing(SHORT_MOTION);
      recordResting(focusedField() !== null);
    };
    const onResize = () => {
      recordResting(focusedField() !== null);
      if (keyboardHeight > 0) measure(SHORT_MOTION);
    };
    // Moving from one field to the next keeps the keyboard up (no new event on
    // Android), so a field taking focus is measured again. With the keyboard
    // closed, a field taking focus is the moment to read the resting size
    // again: the keyboard has not appeared yet, so even a shorter size is the
    // real one, unless focus came from another field or the keyboard is still
    // going down (then a shorter size can be the keyboard's doing). The size
    // read at mount can be out of date, because the app can turn itself while
    // this page mounts (a tablet started upright turns to landscape, a phone
    // started sideways turns upright) and that turn does not always reach the
    // page as a resize.
    const onFocusIn = (event: FocusEvent) => {
      if (keyboardHeight > 0) measure(SHORT_MOTION);
      else recordResting(keyboardClosing || isTextEntry(event.relatedTarget));
    };
    // A turn read as a turn, for the same reason. The new size can land a
    // frame after the event, so it is read then too.
    let orientationFrame = 0;
    const onOrientationChange = () => {
      if (keyboardHeight > 0) return;
      recordResting(focusedField() !== null);
      cancelAnimationFrame(orientationFrame);
      orientationFrame = requestAnimationFrame(() => {
        if (keyboardHeight <= 0) recordResting(focusedField() !== null);
      });
    };
    const screenOrientation = typeof screen === 'undefined' ? undefined : screen.orientation;
    // Leaving the app with the keyboard up may never send keyboardDidHide.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      keyboardGoing(SHORT_MOTION);
      show(PAGE_AT_REST);
    };

    window.addEventListener(KEYBOARD_WILL_SHOW, onWillShow);
    window.addEventListener(KEYBOARD_DID_SHOW, onDidShow);
    window.addEventListener(KEYBOARD_WILL_HIDE, onWillHide);
    window.addEventListener(KEYBOARD_DID_HIDE, onDidHide);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);
    screenOrientation?.addEventListener('change', onOrientationChange);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('touchmove', userScrolled, { capture: true, passive: true });
    document.addEventListener('wheel', userScrolled, { capture: true, passive: true });
    return () => {
      window.removeEventListener(KEYBOARD_WILL_SHOW, onWillShow);
      window.removeEventListener(KEYBOARD_DID_SHOW, onDidShow);
      window.removeEventListener(KEYBOARD_WILL_HIDE, onWillHide);
      window.removeEventListener(KEYBOARD_DID_HIDE, onDidHide);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      screenOrientation?.removeEventListener('change', onOrientationChange);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('touchmove', userScrolled, { capture: true });
      document.removeEventListener('wheel', userScrolled, { capture: true });
      cancelAnimationFrame(orientationFrame);
      cancelAnimationFrame(showFrame);
      cancelAnimationFrame(slideFrame);
      clearTimeout(restTimer);
      formObserver?.disconnect();
    };
  }, [pageRef, scrollFirst]);
};
